import { Box, IconButton, useTheme } from "@mui/material";
import { Dataset, DatasetList } from "data/models";
import { fetchDatasetsLink } from "data/queries";
import { formatBytes } from "utils/commons";
import { formatDate } from "utils";
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { usePostHog } from "@posthog/react";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { formatProductName } from "pages/geoscape-admin/manage-release/new-release/utils";
import { CircleCheck, CircleX, Download } from "lucide-react";

type Dictionary = {
  [key: string]: string;
};

const DisplayFormatMapping: Dictionary = {
  GDB: "GDB",
  GEOJSON: "GeoJSON",
  SHP: "Shapefile",
  TAB: "MapInfo TAB",
  LYNX: "LYNX",
  PUMP: "PUMP",
  GEOTIFF: "GEOTIFF",
  PSV: "Pipe-Separated Values",
};

const DisplayStateMapping: Dictionary = {
  "ALL STATES": "All States",
  AUSTRALIA: "Australia",
};

const customColumns = (): GridColDef[] => {
  const theme = useTheme();

  const { enqueueLinkCorruptedSnackbar } = useCustomSnackbars();
  const posthog = usePostHog();
  return [
    {
      flex: 1.5,
      field: "product",
      headerName: "Product",
      renderCell: (params: GridRenderCellParams) => {
        return params.row.parentProduct ? params.row.product : params.row.name;
      },
    },
    {
      flex: 1.2,
      field: "insight",
      headerName: "Insight Pack",
    },
    {
      flex: 1.2,
      field: "release",
      headerName: "Release",
      sortable: false,
      valueFormatter(params) {
        const [year, month] = (params as string).split("-").map(Number);
        const monthName = new Date(year, month - 1).toLocaleString("default", {
          month: "long",
        });
        return `${monthName} ${year}`;
      },
    },
    {
      flex: 1,
      field: "region",
      headerName: "Region",
      valueFormatter(params) {
        return DisplayStateMapping[params as string] ?? (params as string);
      },
    },
    {
      flex: 1,
      field: "format",
      headerName: "Format",
      valueFormatter(params) {
        return DisplayFormatMapping[params as string] ?? (params as string);
      },
    },
    {
      flex: 1,
      field: "datum",
      headerName: "Datum",
    },
    {
      flex: 1,
      field: "size",
      headerName: "File Size",
      valueFormatter(params) {
        return formatBytes(params as number);
      },
    },

    {
      flex: 1,
      field: "publishDate",
      headerName: "Publish Date",
      sortable: false,
      valueFormatter(params) {
        return formatDate(params as string);
      },
    },
    {
      flex: 1,
      field: "licenced",
      headerName: "Licenced",
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Box
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <CircleCheck size={32} color={theme.palette.success.main} />
          </Box>
        ) : (
          <Box
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <CircleX size={32} color={theme.palette.error.main} />
          </Box>
        ),
    },
    {
      flex: 0.5,
      field: "id",
      headerName: "",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
          >
            {params.row.licenced ? (
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    const response = await fetchDatasetsLink(params.value);

                    const a = document.createElement("a");
                    a.href = response;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(response);
                    posthog.capture("user_downloaded_dataset", {
                      id: params.value,
                      name: params.row.name,
                      region: params.row.region,
                      release: params.row.release,
                      datum: params.row.datum,
                      format: params.row.format,
                    });
                  } catch (er) {
                    enqueueLinkCorruptedSnackbar();
                    // If this happens it will show and popup
                  }
                }}
              >
                <Download size={24} color={theme.palette.primary.main} />
              </IconButton>
            ) : (
              <IconButton disabled>
                <Download size={24} color={theme.palette.text.disabled} />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];
};

const customersToRows = (datasetsList: DatasetList): GridValidRowModel[] => {
  const rows = datasetsList.datasets.map((key: Dataset) => {
    return {
      id: key.id,
      name: key.name,
      parentProduct: key.parentProduct,
      product: !!key.parentProduct
        ? formatProductName(key.parentProduct)
        : formatProductName(key.product),
      insight: key.parentProduct ? formatProductName(key.product) : "-",
      region: key.region,
      format: key.format,
      size: key.size,
      release: key.release,
      publishDate: key.publishDate,
      licenced: key.licenced,
      datum: key.datum,
    };
  });
  return rows;
};

export { customColumns, customersToRows };
